"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.orderLinesToJSON = void 0;
/* eslint-disable @typescript-eslint/no-explicit-any */
const _ = require("lodash");
const currencyFormatter_1 = require("../currencyFormatter");
const orders_1 = require("../orders");
const dayjs = require("dayjs");
const pluralize_1 = require("../pluralize");
const expandOrderLines_1 = require("./expandOrderLines");
const expandedItemProcessor_1 = require("./expandedItemProcessor");
const getPayAtCourseExtrasSummary_1 = require("./getPayAtCourseExtrasSummary");
const groupOrderLinesByDate_1 = require("./groupOrderLinesByDate");
const getPerPersonPricingSummary_1 = require("./getPerPersonPricingSummary");
const helpers_1 = require("../../helpers");
const orderLinesToJSON = (order, variant, exchange_rate, transaction_fee, show_pricing, hide_cancelled // ised as a flag to hide cancelled order lines (e.g. client portal)
) => {
    // Early validation of required objects
    if (!order || !variant?.order_lines)
        return null;
    const getOrderLinesByType = (orderLines, type) => {
        return orderLines.filter((line) => line.type === type);
    };
    const orderLines = variant.order_lines.filter((line) => {
        // Early return if no versions
        if (!line?.versions?.length)
            return false;
        const latestVersion = (0, orders_1.getLatestOrderLineVersion)(line.versions);
        if (!latestVersion?.status)
            return false;
        // Check cancellation status only if necessary
        if (hide_cancelled &&
            order.status &&
            !order.status.includes("CANCELLED") &&
            latestVersion.status.includes("CANCELLED")) {
            return false;
        }
        return true;
    });
    let expanded_order_lines = [];
    let expanded_summary_order_lines = [];
    const orderLineLegNumbers = getOrderLineLegNumbers(orderLines);
    // expand the order lines
    expanded_order_lines = (0, expandOrderLines_1.expandOrderLines)(orderLineLegNumbers, true);
    expanded_summary_order_lines = (0, expandOrderLines_1.expandOrderLines)(orderLineLegNumbers, false);
    const lines = (0, expandedItemProcessor_1.expandedItemProcessor)(expanded_order_lines, order, exchange_rate);
    const summary_lines = (0, expandedItemProcessor_1.expandedItemProcessor)(expanded_summary_order_lines, order, exchange_rate);
    const region_order = getDateRegionIds(lines.order_lines);
    let grouped_lines = (0, groupOrderLinesByDate_1.groupOrderLinesByDate)(lines.order_lines);
    grouped_lines = processLineOrdering(grouped_lines, region_order);
    // get all the order_lines arrays and flatten them into one array
    let flattened_lines = grouped_lines.map((line) => line.order_lines);
    flattened_lines = [].concat(...flattened_lines);
    const golfLines = getOrderLinesByType(flattened_lines, "GOLF");
    const extrasLines = getOrderLinesByType(flattened_lines, "CUSTOM");
    const hotelLines = getOrderLinesByType(flattened_lines, "HOTEL");
    let pga_pros = null;
    if (hotelLines.length > 0) {
        const hotelLine = hotelLines[0];
        const config = hotelLine.data;
        if (config) {
            pga_pros = config?.where_when_who?.pga_pros;
        }
    }
    const per_person_pricing = (0, getPerPersonPricingSummary_1.getPerPersonPricingSummary)(pga_pros, flattened_lines, exchange_rate);
    const variant_summary = getVariantSummary(order, pga_pros, exchange_rate, summary_lines, show_pricing, variant);
    const payAtCourseExtras = (0, getPayAtCourseExtrasSummary_1.getPayAtCourseExtrasSummary)(golfLines, exchange_rate);
    const pricingBands = (0, orders_1.getVariantPricingBands)(variant);
    const totalVariantValue = (0, orders_1.getTotalVariantValue)(variant);
    const variantCurrency = (0, orders_1.getVariantCurrency)(variant);
    const orderDisplayCurrency = (0, orders_1.getOrderDisplayCurrency)(exchange_rate);
    const variantDisplayTotal = (0, orders_1.getVariantDisplayTotal)(variant, exchange_rate);
    const processingFee = totalVariantValue * ((transaction_fee || 0) / 100);
    const processingFeeFormatted = (0, currencyFormatter_1.currencyFormatter)(variantCurrency || "", processingFee || 0, false);
    const totalPriceFormatted = (0, currencyFormatter_1.currencyFormatter)(variantCurrency || "", totalVariantValue, false);
    const displayTotalPriceFormatted = (0, currencyFormatter_1.currencyFormatter)(orderDisplayCurrency, variantDisplayTotal, false);
    return {
        order_id: order.id,
        agent: order?.agent,
        client: order?.client,
        order_status: order.status,
        variant: variant_summary,
        itinerary: {
            items: grouped_lines,
            extras: extrasLines,
            fees: {
                credit_card_fee: transaction_fee || 0,
            },
            per_person_pricing: per_person_pricing,
            pay_at_course_extras: payAtCourseExtras,
            pricing_bands: pricingBands,
            total: {
                currency: variantCurrency,
                processing_fee: processingFee,
                processing_fee_formatted: processingFeeFormatted,
                total_price: totalVariantValue,
                total_price_formatted: totalPriceFormatted,
                display_currency: orderDisplayCurrency,
                display_total_price: variantDisplayTotal,
                display_total_price_formatted: displayTotalPriceFormatted,
            },
        },
    };
};
exports.orderLinesToJSON = orderLinesToJSON;
const getDateRegionIds = (orderLines) => {
    const items = _.cloneDeep(orderLines.filter((line) => line.line_type === "LINE_ITEM"));
    // Sort items by start date
    const sortedItems = [...items].sort((a, b) => {
        const latest_version_a = a.versions
            ? (0, orders_1.getLatestOrderLineVersion)(a.versions)
            : a.latest_version;
        const latest_version_b = b.versions
            ? (0, orders_1.getLatestOrderLineVersion)(b.versions)
            : b.latest_version;
        const dateComparison = new Date(latest_version_a?.start_date).getTime() -
            new Date(latest_version_b?.start_date).getTime();
        return dateComparison;
    });
    const dateRegionMap = new Map();
    // First pass: collect all region IDs per date
    sortedItems.forEach((item) => {
        const latest_version = item.versions
            ? (0, orders_1.getLatestOrderLineVersion)(item.versions)
            : item.latest_version;
        const start_date = latest_version?.start_date;
        const region_id = latest_version?.region_id;
        if (!dateRegionMap.has(start_date)) {
            dateRegionMap.set(start_date, new Set());
        }
        dateRegionMap.get(start_date)?.add(region_id);
    });
    // Convert to array and sort region_ids based on previous and next dates
    const dateArray = Array.from(dateRegionMap.entries())
        .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
        .map(([date, region_ids], index, array) => {
        const regionIdsArray = Array.from(region_ids);
        // If there's only one region ID, no need to sort
        if (regionIdsArray.length <= 1) {
            return {
                date,
                region_ids: regionIdsArray,
            };
        }
        // Get previous and next dates' region IDs
        const prevDate = index > 0 ? array[index - 1] : null;
        const nextDate = index < array.length - 1 ? array[index + 1] : null;
        // Sort region IDs
        const sortedRegionIds = regionIdsArray.sort((a, b) => {
            const aInPrev = prevDate && Array.from(prevDate[1]).includes(a);
            const bInPrev = prevDate && Array.from(prevDate[1]).includes(b);
            const aInNext = nextDate && Array.from(nextDate[1]).includes(a);
            const bInNext = nextDate && Array.from(nextDate[1]).includes(b);
            // If a is in previous date and b isn't, a should come first
            if (aInPrev && !bInPrev)
                return -1;
            if (!aInPrev && bInPrev)
                return 1;
            // If neither is in previous date but one is in next date,
            // the one not in next date should come first
            if (!aInPrev && !bInPrev) {
                if (!aInNext && bInNext)
                    return -1;
                if (aInNext && !bInNext)
                    return 1;
            }
            // If all else is equal, maintain original order
            return 0;
        });
        return {
            date,
            region_ids: sortedRegionIds,
        };
    });
    return dateArray;
};
const getOrderLineLegNumbers = (orderLines) => {
    const items = _.cloneDeep(orderLines);
    const region_order = getDateRegionIds(items);
    // Sort items by start date
    const sortedItems = [...items].sort((a, b) => {
        if (!a.versions || !b.versions)
            return 0;
        const latest_version_a = (0, orders_1.getLatestOrderLineVersion)(a.versions);
        const latest_version_b = (0, orders_1.getLatestOrderLineVersion)(b.versions);
        if (!latest_version_a || !latest_version_b)
            return 0;
        // First compare by date
        const dateComparison = new Date(latest_version_a.start_date).getTime() -
            new Date(latest_version_b.start_date).getTime();
        if (dateComparison !== 0)
            return dateComparison;
        // If same date, check region order from region_order array
        const dateRegionEntry = region_order.find((entry) => entry.date === latest_version_a.start_date);
        if (dateRegionEntry && dateRegionEntry.region_ids.length > 1) {
            // If multiple regions for this date, sort by region order in the array
            const regionIndexA = dateRegionEntry.region_ids.indexOf(latest_version_a.region_id);
            const regionIndexB = dateRegionEntry.region_ids.indexOf(latest_version_b.region_id);
            if (regionIndexA !== regionIndexB)
                return regionIndexA - regionIndexB;
        }
        // If same region or no specific region order, sort by type
        const typeOrder = ["TRANSFER", "GOLF", "EXCURSION", "HOTEL"];
        return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
    let leg_number = 0;
    let previous_region_id = null;
    // Assign leg numbers based on region changes
    sortedItems.forEach((item) => {
        if (!item.versions)
            return;
        const latest_version = (0, orders_1.getLatestOrderLineVersion)(item.versions);
        if (!latest_version)
            return;
        const current_region_id = latest_version.region_id;
        // If this is the first item or if the region has changed, increment the leg number
        if (current_region_id !== previous_region_id) {
            leg_number++;
        }
        // Assign the leg number to the item
        item.leg_number = leg_number;
        // Update the previous region for the next iteration
        previous_region_id = current_region_id;
    });
    return sortedItems;
};
const processLineOrdering = (grouped_lines, region_order) => {
    return grouped_lines.map((group) => {
        const date = group.date;
        const region_ordering = region_order.find((entry) => entry.date === date)?.region_ids || [];
        const sorted_lines = sortLinesByDate(group.order_lines, region_ordering);
        return {
            ...group,
            order_lines: sorted_lines,
        };
    });
};
const sortLinesByDate = (lines, region_order) => {
    return lines.sort((a, b) => {
        // For the same leg_number, sort based on the region_order array
        const regionIndexA = region_order.indexOf(a.region_id);
        const regionIndexB = region_order.indexOf(b.region_id);
        return regionIndexA - regionIndexB;
    });
};
const filterCancelledLines = (lines) => {
    return lines.filter((line) => {
        return !helpers_1.CANCELLED_ORDER_STATUSES.includes(line.status);
    });
};
const getVariantSummary = (order, pga_pros, exchange_rate, lines, show_pricing, variant) => {
    const number_of_rounds = (0, orders_1.getVariantNumberOfRounds)(variant);
    const number_of_nights = (0, orders_1.getVariantNumberOfNights)(variant);
    const first_date = (0, orders_1.getVariantFirstDate)(variant);
    const last_date = dayjs(first_date)
        .add(number_of_nights, "day")
        .toISOString();
    const grouped = groupItemsIntoLegs(lines.order_lines);
    const per_person_pricing = (0, getPerPersonPricingSummary_1.getPerPersonPricingSummary)(pga_pros || null, lines.order_lines, exchange_rate);
    return {
        id: variant.id,
        region_id: (0, orders_1.getVariantRegion)(variant),
        name: variant.name,
        config: variant.config,
        stats: {
            max_pax: (0, orders_1.getVariantMaxPax)(variant),
            max_pax_golf: (0, orders_1.getVariantMaxGolfPax)(variant),
            max_pax_hotel: (0, orders_1.getVariantMaxPaxByType)(variant, "hotel"),
            first_date: first_date,
            last_date: (0, orders_1.getVariantLastDate)(variant),
        },
        summary: `${number_of_nights} nights/${number_of_rounds} rounds`,
        ai_summary: variant.summary,
        ref: variant.ref,
        start_date: dayjs(first_date).format("ddd, Do MMM YYYY"),
        end_date: dayjs(last_date).format("ddd, Do MMM YYYY"),
        order_reference: order.reference,
        non_golfers: (0, pluralize_1.pluralize)(Math.max(0, (0, orders_1.getVariantMaxPaxByType)(variant, "hotel") -
            (0, orders_1.getVariantMaxPaxByType)(variant, "golf")), "non-golfer", "non-golfers"),
        number_of_golfers: (0, pluralize_1.pluralize)((0, orders_1.getVariantMaxPaxByType)(variant, "golf"), "golfer", "golfers"),
        per_person_pricing: per_person_pricing,
        pricing_bands: (0, orders_1.getVariantPricingBands)(variant),
        regions: grouped?.legs.map((group) => {
            const group_lines = group.order_lines.filter((line) => line.line_type === "LINE_ITEM");
            const duration_dates = (0, orders_1.getOrderLinesStartEndDate)(filterCancelledLines(group_lines));
            const first_date = duration_dates.start;
            const last_date = duration_dates.end;
            // get the first and last date
            const golf_lines_raw = group_lines.filter((line) => line.type === "GOLF");
            const hotel_lines_raw = group_lines.filter((line) => line.type === "HOTEL");
            // const transfer_lines_raw = group_lines
            //   .filter((line: any) => line.type === "TRANSFER")
            //   .filter((line: any) => line.latest_version.config.vehicle.vehicle);
            const excursion_lines_raw = group_lines.filter((line) => line.type === "EXCURSION");
            const custom_lines_raw = group_lines.filter((line) => line.type === "CUSTOM");
            const golf_lines = filterCancelledLines(group_lines).filter((line) => line.type === "GOLF");
            const hotel_lines = filterCancelledLines(group_lines).filter((line) => line.type === "HOTEL");
            const transfer_lines = filterCancelledLines(group_lines)
                .filter((line) => line.type === "TRANSFER")
                .filter((line) => line.latest_version.config.vehicle.vehicle);
            const excursion_lines = filterCancelledLines(group_lines).filter((line) => line.type === "EXCURSION");
            const custom_lines = filterCancelledLines(group_lines).filter((line) => line.type === "CUSTOM");
            // get the number of nights between the first and last date
            const number_of_nights = dayjs(last_date).diff(dayjs(first_date), "days");
            const hotel_line_summary = hotel_lines
                .map((h) => {
                const config = h.latest_version?.config;
                return `${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_nights, "night", "nights")}, ${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_guests, "guest", "guests")}`;
            })
                .join(" / ");
            const region_summary = () => {
                if (hotel_lines.length > 0 && golf_lines.length > 0) {
                    return `${number_of_nights}N/${golf_lines.length}R`;
                }
                else if (hotel_lines.length > 0) {
                    return `${number_of_nights}N`;
                }
                else if (golf_lines.length > 0) {
                    return `${golf_lines.length}R`;
                }
            };
            const region_summary_long = () => {
                if (hotel_lines.length > 0 && golf_lines.length > 0) {
                    return `${(0, pluralize_1.pluralize)(number_of_nights, "night", "nights")}/${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")}`;
                }
                else if (hotel_lines.length > 0) {
                    return `${(0, pluralize_1.pluralize)(number_of_nights, "night", "nights")}`;
                }
                else if (golf_lines.length > 0) {
                    return `${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")}`;
                }
            };
            return {
                name: group.region.name.en,
                start_date: dayjs(first_date).format("ddd, Do MMM YYYY"),
                end_date: dayjs(last_date).format("ddd, Do MMM YYYY"),
                summary: region_summary(),
                summary_long: region_summary_long(),
                golf: {
                    enabled: golf_lines.length > 0,
                    total: golf_lines.length,
                    title: `Golf in ${group.region.name.en} (${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")})`,
                    summary: `${(0, pluralize_1.pluralize)(golf_lines.length, "round", "rounds")} of golf`,
                    tee_times: golf_lines.map((line) => {
                        const config = line.latest_version.config;
                        const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                        const course = {
                            id: config?.course?.id,
                            latest_version: line.latest_version,
                            name: config?.course?.name,
                            date: date,
                            tee_time: config?.tee_time,
                            tee_time_range: {
                                earliest: config?.green_fee?.earliest_time,
                                latest: config?.green_fee?.latest_time,
                            },
                        };
                        return course;
                    }),
                    rounds: golf_lines_raw.map((line) => {
                        const config = line.latest_version.config;
                        const description = (pga_pros?.count || 0) > 0
                            ? (0, orders_1.getOrderLineConfigPGADescriptions)(config, "GOLF")
                            : (0, orders_1.getOrderLineConfigClientDescriptions)(config, "GOLF");
                        const line_total = (0, orders_1.getOrderLineTotalByType)(config, "GOLF");
                        const per_person_total = line_total / config?.where_when_who?.number_of_golfers;
                        if (config?.green_fee?.odd_number_surcharge &&
                            config?.where_when_who?.number_of_golfers % 2 !== 0) {
                            description?.unshift(`Includes a surcharge of ${(0, currencyFormatter_1.currencyFormatter)(line?.currency, config?.green_fee?.odd_number_surcharge, false)} as one player will get their own cart`);
                        }
                        if (config?.green_fee?.rate_note) {
                            description?.unshift(config?.green_fee?.rate_note);
                        }
                        if (description && config?.product?.name?.en)
                            description.unshift(`${getGolfName(config)} ${show_pricing
                                ? ` · ${(0, currencyFormatter_1.currencyFormatter)(line?.currency, per_person_total, false)} pp`
                                : ""}`);
                        const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                        const course = {
                            id: config?.course?.id,
                            name: config?.course?.name,
                        };
                        return {
                            title: `${date}: ${course?.name?.en}`,
                            latest_version: line.latest_version,
                            date: date,
                            course: course,
                            order_line: line,
                            descriptions: description,
                        };
                    }),
                    footnote: "",
                },
                hotels: {
                    enabled: hotel_lines.length > 0,
                    total: hotel_lines.length,
                    title: `Accommodation in 
            ${group.region.name.en} (${hotel_line_summary}) `,
                    stays: hotel_lines_raw.map((line) => {
                        const config = line.latest_version.config;
                        const dates = `${dayjs(config?.where_when_who?.check_in_date).format("ddd, Do MMM")} - ${dayjs(config?.where_when_who?.check_in_date)
                            .add(config?.where_when_who?.number_of_nights, "day")
                            .format("ddd, Do MMM")}`;
                        const nights = (0, pluralize_1.pluralize)(config?.where_when_who?.number_of_nights, "night", "nights");
                        const hotel = {
                            id: config?.where_when_who?.hotel?.id,
                            name: config?.where_when_who?.hotel?.name,
                            star_rating: config?.where_when_who?.hotel?.star_rating,
                            nights: nights,
                        };
                        return {
                            title: `${dates}: ${hotel?.name?.en} ${hotel.star_rating && `(${hotel.star_rating}*)`}`,
                            order_line: line,
                            hotel: hotel,
                            date: dates,
                            descriptions: getHotelLineDescriptions(config),
                            warnings: getHotelLineWarnings(config),
                            check_in_out: `Check-in: ${config?.where_when_who?.hotel.check_in_time} / Check-out: ${config?.where_when_who?.hotel.check_out_time}`,
                        };
                    }),
                },
                transfers: {
                    enabled: transfer_lines.length > 0,
                    title: `${transfer_lines.length > 1 ? "Transfers" : "Transfer"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(transfer_lines.length, "trip", "trips")})`,
                    description: `${(0, pluralize_1.pluralize)(transfer_lines.length, "trip", "trips")} in an air-conditioned, private vehicle`,
                    total: transfer_lines.length,
                    trips: convertTransfers(transfer_lines),
                },
                excursions: {
                    enabled: excursion_lines.length > 0,
                    total: excursion_lines.length,
                    title: excursion_lines.length === 0
                        ? `Excursions in ${group.region.name.en}`
                        : `${excursion_lines.length > 1 ? "Excursions" : "Excursion"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(excursion_lines.length, "excursion", "excursions")})`,
                    description: `${(0, pluralize_1.pluralize)(excursion_lines.length, "excursion", "excursions")}`,
                    trips: excursion_lines_raw.map((line) => {
                        const config = line.latest_version.config;
                        const date = dayjs(config.where_when_who?.date).format("ddd, Do MMM");
                        const excursion = {
                            id: config?.product?.excursion?.id,
                            name: config?.product?.excursion?.name,
                        };
                        return {
                            title: `${date}: ${excursion?.name?.en}`,
                            date: date,
                            order_line: line,
                            excursion: excursion,
                            descriptions: (0, orders_1.getOrderLineConfigDescriptions)(config, "EXCURSION"),
                        };
                    }),
                },
                custom: {
                    enabled: custom_lines.length > 0,
                    total: custom_lines.length,
                    title: `${custom_lines.length > 1 ? "Custom" : "Custom"} in ${group.region.name.en} (${(0, pluralize_1.pluralize)(custom_lines.length, "item", "items")})`,
                    description: `${(0, pluralize_1.pluralize)(custom_lines.length, "item", "items")}`,
                    items: custom_lines_raw.map((line) => {
                        const config = line.latest_version.config;
                        return {
                            title: config?.product?.name?.en,
                            order_line: line,
                            descriptions: (0, orders_1.getOrderLineConfigDescriptions)(config, "CUSTOM"),
                        };
                    }),
                },
            };
        }),
    };
};
function groupItemsIntoLegs(items) {
    // Sort items by start date to ensure proper ordering
    const region_order = getDateRegionIds(items);
    // Sort items by start date
    const sortedItems = [...items].sort((a, b) => {
        const dateComparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        if (dateComparison !== 0)
            return dateComparison;
        // If same date, check region order from region_order array
        const dateRegionEntry = region_order.find((entry) => entry.date === a.start_date);
        if (dateRegionEntry && dateRegionEntry.region_ids.length > 1) {
            // If multiple regions for this date, sort by region order in the array
            const regionIndexA = dateRegionEntry.region_ids.indexOf(a.region_id);
            const regionIndexB = dateRegionEntry.region_ids.indexOf(b.region_id);
            if (regionIndexA !== regionIndexB)
                return regionIndexA - regionIndexB;
        }
        // If same region or no specific region order, sort by type
        const typeOrder = ["TRANSFER", "GOLF", "EXCURSION", "HOTEL"];
        return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
    const legs = [];
    let currentLeg = null;
    sortedItems.forEach((item, index) => {
        // If this is the first item or we've changed regions
        if (!currentLeg || currentLeg.region.id !== item.region.id) {
            // Close out the previous leg if it exists
            if (currentLeg) {
                currentLeg.endDate = item.start_date;
            }
            // Start a new leg
            currentLeg = {
                region: item.region,
                startDate: item.start_date,
                endDate: "",
                order_lines: [],
            };
            legs.push(currentLeg);
        }
        // Add item to current leg
        currentLeg.order_lines.push(item);
        // If this is the last item, set the end date
        if (index === sortedItems.length - 1) {
            currentLeg.endDate = item.start_date;
        }
    });
    return { legs };
}
function getHotelLineWarnings(config) {
    const warnings = new Set();
    config.rooms.rooms.forEach((room) => {
        room.rate.forEach((rate) => {
            if (rate?.warnings) {
                rate.warnings.forEach((warning) => {
                    warnings.add(warning);
                });
            }
        });
    });
    return Array.from(warnings);
}
function getHotelLineDescriptions(config) {
    const descriptions = [];
    const grouped_rooms = (0, orders_1.groupRoomsByTypeAndBoard)(config.rooms.rooms);
    grouped_rooms.forEach((room) => {
        let occupancy = room.occupancy;
        if (room.extra_beds > 0) {
            occupancy += room.extra_beds;
        }
        descriptions.push({
            title: `${room.quantity} x ${room.name} ${room.extra_beds > 0
                ? ` + ${(0, pluralize_1.pluralize)(room.extra_beds, "extra bed", "extra beds")}`
                : ""} - ${(0, pluralize_1.pluralize)(occupancy, "guest", "guests")}`,
            quantity: room.quantity,
            extras: room.extra_beds > 0
                ? ` + ${(0, pluralize_1.pluralize)(room.extra_beds, "extra bed", "extra beds")}`
                : "",
            room: room,
            occupancy_raw: occupancy,
            occupancy: (0, pluralize_1.pluralize)(occupancy, "guest", "guests"),
        });
    });
    return descriptions;
}
function convertTransfers(transfers) {
    const transfer_lines = [];
    if (transfers.length > 3) {
        const golf_transfers = transfers.filter((transfer) => transfer.latest_version.config.transfer_legs.outbound.to_type ===
            "Course" ||
            transfer.latest_version.config.transfer_legs.inbound.from_type ===
                "Course");
        const all_golf_same = checkMatchingTransfers(golf_transfers.map((t) => t.latest_version.config));
        transfers.forEach((transfer) => {
            const { config } = transfer.latest_version;
            const { outbound, inbound } = config.transfer_legs;
            const { vehicle } = config;
            if (!vehicle?.vehicle)
                return;
            const region = transfer?.region;
            const is_custom = config.where_when_who.type === "CUSTOM";
            let is_airport_transfer = false;
            if (outbound.to_type === "Airport" ||
                outbound.from_type === "Airport" ||
                inbound.to_type === "Airport" ||
                inbound.from_type === "Airport") {
                is_airport_transfer = true;
            }
            if ((outbound.to_type === "Area" ||
                outbound.from_type === "Area" ||
                outbound.to_type === "Hotel" ||
                outbound.from_type === "Hotel") &&
                outbound.to_type !== "Course") {
                // Hotel transfer
                transfer_lines.push({
                    title: `One-way transfer from ${outbound.from_name} to ${outbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                    date: config.where_when_who.date
                        ? dayjs(config.where_when_who.date).format("ddd, Do MMM YYYY")
                        : "",
                    journey: `One-way transfer from ${outbound.from_name} to ${outbound.to_name}`,
                    passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                    airport_transfer: is_airport_transfer,
                    order_line: transfer,
                    descriptions: [],
                    vehicle: vehicle?.vehicle
                        ? {
                            id: vehicle?.vehicle?.id,
                            name: vehicle?.vehicle?.name,
                            image: typeof vehicle?.vehicle?.image === "string"
                                ? JSON.parse(vehicle?.vehicle?.image)
                                : vehicle?.vehicle?.image,
                        }
                        : undefined,
                });
            }
            else if (outbound.to_type === "Course" ||
                inbound.from_type === "Course") {
                if (all_golf_same &&
                    golf_transfers.length > 0 &&
                    config?.where_when_who.type !== "CUSTOM") {
                    transfer_lines.push({
                        title: `Roundtrip transfers from ${outbound.from_name} to all ${region?.name?.en} golf courses in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                        date: config.where_when_who.date
                            ? dayjs(config.where_when_who.date).format("ddd, Do MMM YYYY")
                            : "",
                        journey: `Roundtrip ${(0, pluralize_1.pluralize)(golf_transfers.length, "transfer", "transfers", true)} from ${outbound.from_name} to ${golf_transfers.length > 1
                            ? `all ${region?.name?.en} golf courses`
                            : golf_transfers[0]?.latest_version?.config?.transfer_legs
                                .outbound.to_name}`,
                        descriptions: [],
                        passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                        airport_transfer: is_airport_transfer,
                        order_line: transfer,
                        vehicle: vehicle?.vehicle
                            ? {
                                id: vehicle?.vehicle?.id,
                                name: vehicle?.vehicle?.name,
                                image: typeof vehicle?.vehicle?.image === "string"
                                    ? JSON.parse(vehicle?.vehicle?.image)
                                    : vehicle?.vehicle?.image,
                            }
                            : undefined,
                    });
                }
                else {
                    if (is_custom) {
                        transfer_lines.push({
                            title: `Custom transfer from ${outbound.from_name} to ${outbound.to_name} to ${inbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                            date: config.where_when_who.date
                                ? dayjs(config.where_when_who.date).format("ddd, Do MMM YYYY")
                                : "",
                            journey: `Custom transfer from ${outbound.from_name} to ${outbound.to_name} to ${inbound.to_name}`,
                            passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                            airport_transfer: is_airport_transfer,
                            order_line: transfer,
                            descriptions: [],
                            vehicle: vehicle?.vehicle
                                ? {
                                    id: vehicle?.vehicle?.id,
                                    name: vehicle?.vehicle?.name,
                                    image: typeof vehicle?.vehicle?.image === "string"
                                        ? JSON.parse(vehicle?.vehicle?.image)
                                        : vehicle?.vehicle?.image,
                                }
                                : undefined,
                        });
                    }
                    else {
                        transfer_lines.push({
                            title: `Roundtrip transfers from ${outbound.from_name} to ${outbound.to_name} in a ${vehicle?.vehicle?.name?.en?.toLowerCase()} (${config?.where_when_who?.number_of_passengers} pax)`,
                            journey: `Roundtrip transfers from ${outbound.from_name} to ${outbound.to_name}`,
                            passengers: `${config?.where_when_who?.number_of_passengers} pax`,
                            airport_transfer: is_airport_transfer,
                            order_line: transfer,
                            descriptions: [],
                            vehicle: vehicle?.vehicle
                                ? {
                                    id: vehicle?.vehicle?.id,
                                    name: vehicle?.vehicle?.name,
                                    image: typeof vehicle?.vehicle?.image === "string"
                                        ? JSON.parse(vehicle?.vehicle?.image)
                                        : vehicle?.vehicle?.image,
                                }
                                : undefined,
                        });
                    }
                }
            }
        });
        const groupedTransfers = transfer_lines.reduce((acc, transfer) => {
            const key = `${transfer.journey}-${transfer.passengers}-${transfer.vehicle?.id}`;
            if (!acc[key]) {
                acc[key] = {
                    ...transfer,
                    order_lines: [transfer.order_line],
                };
            }
            else {
                acc[key].order_lines.push(transfer.order_line);
            }
            return acc;
        }, {});
        return Object.values(groupedTransfers);
    }
    else {
        // For less than 4 transfers, show individual transfers without grouping
        transfers.forEach((transfer) => {
            const { config } = transfer.latest_version;
            const { outbound, inbound } = config.transfer_legs;
            const { vehicle } = config;
            if (!vehicle?.vehicle)
                return;
            const is_custom = config.where_when_who.type === "CUSTOM";
            let is_airport_transfer = false;
            if (outbound.to_type === "Airport" ||
                outbound.from_type === "Airport" ||
                inbound.to_type === "Airport" ||
                inbound.from_type === "Airport") {
                is_airport_transfer = true;
            }
            // Format date for individual transfers
            const transfer_date = config.where_when_who.date
                ? dayjs(config.where_when_who.date).format("ddd, Do MMM YYYY")
                : "";
            if ((outbound.to_type === "Area" ||
                outbound.from_type === "Area" ||
                outbound.to_type === "Hotel" ||
                outbound.from_type === "Hotel") &&
                outbound.to_type !== "Course") {
                // Hotel transfer
                transfer_lines.push({
                    title: `${transfer_date}`,
                    journey: `One-way transfer from ${outbound.from_name} to ${outbound.to_name}`,
                    passengers: `${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_passengers, "passenger", "passengers")}`,
                    airport_transfer: is_airport_transfer,
                    latest_version: transfer.latest_version,
                    order_line: transfer,
                    descriptions: (0, orders_1.getOrderLineConfigClientDescriptions)(config, "TRANSFER"),
                    vehicle: vehicle?.vehicle
                        ? {
                            id: vehicle?.vehicle?.id,
                            name: vehicle?.vehicle?.name,
                            image: typeof vehicle?.vehicle?.image === "string"
                                ? JSON.parse(vehicle?.vehicle?.image)
                                : vehicle?.vehicle?.image,
                        }
                        : undefined,
                });
            }
            else if (outbound.to_type === "Course" ||
                inbound.from_type === "Course") {
                if (is_custom) {
                    transfer_lines.push({
                        title: `${transfer_date}`,
                        journey: `Custom transfer from ${outbound.from_name} to ${outbound.to_name} to ${inbound.to_name}`,
                        passengers: `${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_passengers, "passenger", "passengers")}`,
                        airport_transfer: is_airport_transfer,
                        latest_version: transfer.latest_version,
                        order_line: transfer,
                        descriptions: (0, orders_1.getOrderLineConfigClientDescriptions)(config, "TRANSFER"),
                        vehicle: vehicle?.vehicle
                            ? {
                                id: vehicle?.vehicle?.id,
                                name: vehicle?.vehicle?.name,
                                image: typeof vehicle?.vehicle?.image === "string"
                                    ? JSON.parse(vehicle?.vehicle?.image)
                                    : vehicle?.vehicle?.image,
                            }
                            : undefined,
                    });
                }
                else {
                    transfer_lines.push({
                        title: `${transfer_date}`,
                        journey: `Roundtrip transfer from ${outbound.from_name} to ${outbound.to_name}`,
                        passengers: `${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_passengers, "passenger", "passengers")}`,
                        airport_transfer: is_airport_transfer,
                        latest_version: transfer.latest_version,
                        descriptions: (0, orders_1.getOrderLineConfigClientDescriptions)(config, "TRANSFER"),
                        order_line: transfer,
                        vehicle: vehicle?.vehicle
                            ? {
                                id: vehicle?.vehicle?.id,
                                name: vehicle?.vehicle?.name,
                                image: typeof vehicle?.vehicle?.image === "string"
                                    ? JSON.parse(vehicle?.vehicle?.image)
                                    : vehicle?.vehicle?.image,
                            }
                            : undefined,
                    });
                }
            }
        });
        // For individual transfers, add the order_lines property but with only a single item
        return transfer_lines.map((transfer) => ({
            ...transfer,
            order_lines: [transfer.order_line],
        }));
    }
}
function checkMatchingTransfers(transfers) {
    if (transfers.length === 0)
        return false;
    // Extract the criteria from the first transfer
    const firstTransfer = transfers[0];
    const numberOfPassengers = firstTransfer.where_when_who.number_of_passengers;
    const vehicleId = firstTransfer?.vehicle?.vehicle?.id;
    const fromArea = firstTransfer.transfer_legs.outbound.from_area;
    // Check if all transfers match the criteria
    return transfers.every((transfer) => {
        return (transfer.where_when_who.number_of_passengers === numberOfPassengers &&
            transfer?.vehicle?.vehicle?.id === vehicleId &&
            transfer.transfer_legs.outbound.from_area === fromArea);
    });
}
const getGolfName = (config) => {
    const hasCaddies = () => {
        if (config?.product?.config?.caddie) {
            return true;
        }
        if (config?.caddies?.enabled &&
            config?.caddies?.payment_option !== "ON_ARRIVAL" &&
            config?.caddies?.type === "SINGLE_CADDIE") {
            return true;
        }
        return false;
    };
    const hasSharedCaddies = () => {
        if (config?.product?.config?.shared_caddie) {
            return true;
        }
        if (config?.caddies?.enabled &&
            config?.caddies?.payment_option !== "ON_ARRIVAL" &&
            config?.caddies?.type === "SHARED_CADDIE") {
            return true;
        }
        return false;
    };
    const hasCarts = () => {
        if (config?.product?.config?.single_cart) {
            return true;
        }
        if (config?.carts?.enabled &&
            config?.carts?.payment_option !== "ON_ARRIVAL" &&
            config?.carts?.types
                ?.filter((x) => x.type === "SINGLE_CART")
                ?.find((x) => x.cost_per_cart > 0 && x.quantity > 0)) {
            return true;
        }
        return false;
    };
    const hasSharedCarts = () => {
        if (config?.product?.config?.shared_cart) {
            return true;
        }
        if (config?.carts?.enabled &&
            config?.carts?.payment_option !== "ON_ARRIVAL" &&
            config?.carts?.types
                ?.filter((x) => x.type === "SHARED_CART")
                ?.find((x) => x.cost_per_cart > 0 && x.quantity > 0)) {
            return true;
        }
        return false;
    };
    return `${config?.product?.config?.holes}  Holes ${hasCaddies() ? `+ Caddie` : ``} ${hasSharedCaddies() ? `+ Shared Caddie` : ``}${hasCarts() ? `+ Cart` : ``} ${hasSharedCarts() ? `+ Shared Cart` : ``} ${config?.product?.type !== "Standard" ? `(${config?.product?.type}) ` : ""}· ${(0, pluralize_1.pluralize)(config.where_when_who?.number_of_golfers, "player", "players")}`;
};
